import { Component, ComponentRef, Input, OnChanges, OnInit, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { IScanSummaryAllItem } from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { AccessibilityAuditToolNames, AuditToolNameToAuditType } from '../../../../../../../shared/constants/audit-tool';
import { $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import { IAuditRule } from '../../../../../../../shared/interfaces/audit-rule.interface';
import { $scanAudit } from '../../../../../../../shared/constants/scan-audit';
import { ExcludedRulesModalComponent } from './excluded-rules-modal/excluded-rules-modal.component';
import { ModalService } from '../../../../../services/modal.service';
import { IAutomatedUserFlowError, IScan } from '../../../../../../../shared/interfaces/scan.interface';
import { $automatedUserFlowError, $scan } from '../../../../../../../shared/constants/scan';
import { IScanAudit } from '../../../../../../../shared/interfaces/scan-audit.interface';
import { scanningType, ScanOrigin } from '../../../../../../../shared/constants/scanning';
import { $scanTag, SystemTags } from '../../../../../../../shared/constants/scan-tag';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { alertType } from '../../../../../constants/alert.constants';
import { Api } from '../../../../../../../shared/constants/api';
import { AclSecurityAdapter } from '../../../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../../shared/constants/security';
import { UserAclService } from '../../../../../services/user-acl.service';
import { AngularUtility } from '../../../../../utility/angular.utility';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { UserDigitalPropertyService } from '../../../../../services/user-digital-property.service';

enum ScanResultsReportView {
  PAGE = 'PAGE',
  RULE = 'RULE',
  COMPONENT = 'COMPONENT',
  SCAN_POINT = 'SCAN_POINT',
}

@Component({
  selector: 'app-view-scan-results-aggregated-issues',
  templateUrl: './view-scan-results-aggregated-issues.component.html',
  styleUrls: ['./view-scan-results-aggregated-issues.component.scss'],
  standalone: false,
})
export class ViewScanResultsAggregatedIssuesComponent implements OnChanges, OnInit {
  private _canViewByComponent: boolean;
  public readComponentMatchingPermission: Observable<boolean>;
  @Input() public scanSummary: IScanSummaryAllItem;
  @Input() public auditTool: AccessibilityAuditToolNames;
  @Input() public scan: IScan;
  @Input() public workspaceId: string;
  @Input() public digitalPropertyId: string;
  @Input() public scanAudit: IScanAudit;
  @Input() public set canViewByComponent(value: boolean) {
    this._canViewByComponent = value;
    if (!value && this.reportView === ScanResultsReportView.COMPONENT) {
      this.reportView = this.isMonitoringScan ? ScanResultsReportView.PAGE : ScanResultsReportView.RULE;
    }
  }
  public getCanViewByComponent(): Observable<boolean> {
    if (!this._canViewByComponent) {
      return of(false);
    }
    return this.readComponentMatchingPermission;
  }

  public get scanIncompleteText(): string {
    return this.featureFlagService.variation(FeatureFlagCollection.userFlowsQ42023Revamp, false)
      ? 'scan_incomplete_user_flow_action_failed_pages'
      : 'scan_incomplete_user_flow_action_failed';
  }

  public get automatedUserFlowError(): [string, string, string] | null {
    const userFlowError: IAutomatedUserFlowError = this.scan[$scan.automatedUserFlowError];

    if (SharedCommonUtility.isNullish(userFlowError)) {
      return null;
    }

    const selector: string = SharedCommonUtility.notNullish(userFlowError[$automatedUserFlowError.actionCssSelector])
      ? userFlowError[$automatedUserFlowError.actionCssSelector]
      : userFlowError[$automatedUserFlowError.actionValue];

    return [userFlowError[$automatedUserFlowError.actionType], selector, userFlowError[$automatedUserFlowError.errorMessage]];
  }

  public ScanResultsReportView: typeof ScanResultsReportView = ScanResultsReportView;
  public $scan: typeof $scan = $scan;
  public hasIgnoredRule: boolean;
  public $isMobile: Signal<boolean> = signal(false);
  public reportView: ScanResultsReportView;
  public scanningType: typeof scanningType = scanningType;
  public alertType: typeof alertType;

  constructor(
    private modalService: ModalService,
    private userAclService: UserAclService,
    private featureFlagService: FeatureFlagService,
    private userDigitalPropertyService: UserDigitalPropertyService,
  ) {
    this.alertType = alertType;
    this.$isMobile = toSignal(this.userDigitalPropertyService.isMobileDigitalProperty$);
  }

  private getHasIgnoredRule(): boolean {
    const rules: Record<string, IAuditRule> =
      this.scanSummary[$scanSummaries.scanAuditsId][$scanAudit.audits][AuditToolNameToAuditType[this.auditTool]].rules;
    return Object.values(rules).filter((rule: IAuditRule) => rule.isSelectedForScanning === false).length > 0;
  }

  private reload(): void {
    this.hasIgnoredRule = this.getHasIgnoredRule();
  }

  private get isGuidedUserFlow(): boolean {
    return this.scan[$scan.tag]?.[$scanTag.tagName] === SystemTags.GuidedUserFlow;
  }

  private get isAutomatedUserFlow(): boolean {
    return this.scan[$scan.scanningType] === scanningType.automatedUserFlow;
  }

  private get showScanPointView(): boolean {
    return this.isAutomatedUserFlow || this.isGuidedUserFlow;
  }

  public openExcludedRulesModal(): void {
    const modalComponentRef: ComponentRef<ExcludedRulesModalComponent> = this.modalService.open(ExcludedRulesModalComponent);
    modalComponentRef.instance.modalInputData = {
      data: {
        scanSummary: this.scanSummary,
        auditTool: this.auditTool,
      },
    };
  }

  public ngOnChanges(): void {
    this.reload();
  }

  public get isMonitoringTag(): boolean {
    return this.scan[$scan.tag]?.[$scanTag.tagName] === SystemTags.Monitoring;
  }

  public get isMonitoringScan(): boolean {
    return this.scan?.[$scan.scanOrigin] === ScanOrigin.monitoring;
  }

  public get basePath(): string {
    if (this.isMonitoringScan) {
      return `/${Api.monitoring}/${Api.components}`;
    }
    return `./`;
  }

  public ngOnInit(): void {
    this.reportView = this.showScanPointView ? ScanResultsReportView.SCAN_POINT : ScanResultsReportView.RULE;
    if (this.reportView === ScanResultsReportView.RULE) {
      this.reportView = this.isMonitoringScan ? ScanResultsReportView.PAGE : ScanResultsReportView.RULE;
    }
    this.readComponentMatchingPermission = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .useFunctionalActions({ [FunctionalArea.component_matching]: [SecureAction.read] })
          .check(),
      ),
      AngularUtility.shareRef<boolean>(),
    );
  }
}
